import {
  InnerContentItem,
  OuterContentItem,
  SubHeading,
} from "@certn/cascadia-react";
import { usePeopleAPI } from "@certn/legacy-api";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { CategoryType } from "./DocumentField";
import { download } from "../../../../components/ApplicantDocumentUpload/utils";

export function IdentificationDocumentsSummaryField({
  field,
}: {
  field: components["schemas"]["IdentificationDocumentsCheckField"];
}) {
  const { t } = useTranslation();
  const { data: documentTypes } = usePeopleAPI<
    "/internal/people/document-types/",
    Record<CategoryType, { key: string; value: string }[]>
  >("/internal/people/document-types/", {
    check: "acic",
  });

  const documents = useMemo(
    () => ({
      commencement: field.value.filter(
        ({ document_subtype }) => document_subtype === "commencement"
      )[0],
      primary: field.value.filter(
        ({ document_subtype }) => document_subtype === "primary"
      )[0],
      secondary: field.value.filter(
        ({ document_subtype }) => document_subtype === "secondary"
      ),
      supporting: field.value.filter(
        ({ document_subtype }) => document_subtype === "supporting"
      ),
    }),
    [field.value]
  );

  if (!documentTypes) {
    return null;
  }

  const getDocumentTypeLabel = (subtype: CategoryType, key: string) =>
    documentTypes[subtype]?.find((type) => type.key === key)?.value ?? "";

  return (
    <>
      <SubHeading>
        <Trans>COMMENCEMENT</Trans>
      </SubHeading>
      <DisplayDocument
        label={getDocumentTypeLabel(
          "commencement",
          documents.commencement?.document_type
        )}
        document={documents.commencement}
        onDownload={download}
        testID="identity-documents-summary-commencement"
      />

      <SubHeading>
        <Trans>PRIMARY</Trans>
      </SubHeading>
      <DisplayDocument
        label={getDocumentTypeLabel(
          "primary",
          documents.primary?.document_type
        )}
        document={documents.primary}
        onDownload={download}
        testID="identity-documents-summary-primary"
      />

      <SubHeading>
        <Trans>SECONDARY - 1 OF 2</Trans>
      </SubHeading>
      <DisplayDocument
        label={getDocumentTypeLabel(
          "secondary",
          documents.secondary[0]?.document_type
        )}
        document={documents.secondary[0]}
        onDownload={download}
        testID="identity-documents-summary-secondary-1"
      />

      <SubHeading>
        <Trans>SECONDARY - 2 OF 2</Trans>
      </SubHeading>
      <DisplayDocument
        label={getDocumentTypeLabel(
          "secondary",
          documents.secondary[1]?.document_type
        )}
        document={documents.secondary[1]}
        onDownload={download}
        testID="identity-documents-summary-secondary-2"
      />

      <SubHeading>
        <Trans>SUPPORTING DOCUMENTS</Trans>
      </SubHeading>
      {documents.supporting.length ? (
        documents.supporting.map((document, i) => (
          <InnerContentItem
            key={i}
            icon="download"
            onIconPress={() => download(document.get_url, document.file_name)}
            testID={`identity-documents-summary-supporting-${i + 1}`}
          >
            {document.file_name}
          </InnerContentItem>
        ))
      ) : (
        <OuterContentItem
          emptyData={t("No documents provided")}
          testID="identity-documents-summary-supporting-none-provided"
        />
      )}
    </>
  );
}

const DisplayDocument = ({
  label,
  document,
  onDownload,
  testID,
}: {
  label: string;
  document?: components["schemas"]["IdentificationDocumentsCheckField"]["value"][0];
  onDownload: (url: string, fileName: string) => void;
  testID?: string;
}) => {
  const { t } = useTranslation();

  if (!document) {
    return (
      <OuterContentItem
        emptyData={t("No documents provided")}
        testID={`${testID}-no-document`}
      />
    );
  }

  return (
    <>
      <InnerContentItem label={t("Document type")} testID={`${testID}-type`}>
        {label}
      </InnerContentItem>

      <InnerContentItem
        label={t("Filename")}
        icon="download"
        onIconPress={() => onDownload(document.get_url, document.file_name)}
        testID={`${testID}-filename`}
      >
        {document.file_name}
      </InnerContentItem>
    </>
  );
};
